import { Controller } from "@hotwired/stimulus"

// Manages appearance (light / dark / system) with a sliding pill indicator.
// Persists choice in localStorage, dispatches `appearance:change` for chart re-renders.
//
// Targets: lightBtn, systemBtn, darkBtn, indicator
// Actions: appearance#light, appearance#system, appearance#dark
//
export default class extends Controller {
  static targets = ["lightBtn", "systemBtn", "darkBtn", "indicator"]

  connect() {
    this._mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    this._onSystemChange = () => {
      if (this._current() === "system") this._apply("system")
    }
    this._mediaQuery.addEventListener("change", this._onSystemChange)

    // Sync all appearance controllers when another one changes the theme
    this._onAppearanceChange = () => this._highlight()
    window.addEventListener("appearance:change", this._onAppearanceChange)

    // Initial highlight (no animation on first paint)
    requestAnimationFrame(() => this._highlight(false))
  }

  disconnect() {
    this._mediaQuery.removeEventListener("change", this._onSystemChange)
    window.removeEventListener("appearance:change", this._onAppearanceChange)
  }

  light() {
    this._set("light")
  }
  system() {
    this._set("system")
  }
  dark() {
    this._set("dark")
  }

  // ── private ──

  _current() {
    return localStorage.getItem("appearance") || "system"
  }

  _set(mode) {
    localStorage.setItem("appearance", mode)
    this._apply(mode)
    this._highlight(true)
    window.dispatchEvent(
      new CustomEvent("appearance:change", { detail: { mode } }),
    )
  }

  _apply(mode) {
    const dark =
      mode === "dark" || (mode === "system" && this._mediaQuery.matches)
    document.documentElement.classList.toggle("dark", dark)
  }

  _highlight(animate = false) {
    const mode = this._current()
    const map = { light: "lightBtn", system: "systemBtn", dark: "darkBtn" }
    let activeEl = null

    for (const [key, targetName] of Object.entries(map)) {
      const hasTarget =
        this[
          `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
        ]
      if (!hasTarget) continue
      const el = this[`${targetName}Target`]
      if (key === mode) {
        el.classList.add("appearance-btn-active")
        el.classList.remove("appearance-btn-inactive")
        activeEl = el
      } else {
        el.classList.remove("appearance-btn-active")
        el.classList.add("appearance-btn-inactive")
      }
    }

    // Slide the pill indicator to the active button
    if (activeEl && this.hasIndicatorTarget) {
      const indicator = this.indicatorTarget
      const container = indicator.parentElement
      const containerRect = container.getBoundingClientRect()
      const activeRect = activeEl.getBoundingClientRect()

      indicator.style.transition = animate
        ? "transform 280ms cubic-bezier(0.4, 0, 0.2, 1), width 280ms cubic-bezier(0.4, 0, 0.2, 1)"
        : "none"
      indicator.style.transform = `translateX(${activeRect.left - containerRect.left}px)`
      indicator.style.width = `${activeRect.width}px`
    }
  }
}
